import { makeStyles } from "@mui/styles"
// import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner"
import React, { useEffect, useState, useRef, forwardRef } from "react"
import { TextField, Button, Stack } from "@mui/material";
import CropFreeIcon from '@mui/icons-material/CropFree';
import useKeyboardTimedInput, { ALPHA_NUMERIC_REGEX } from "./useKeyboardInput"
import { useValidateSPLot } from "../api/vegApl/vegApl";
import { useSnackbar } from "../contexts/SnackbarContext";
import { useCommonStyles } from "../styles/GenericStyle";
import { MtComStyleObj } from '../styles/MtComStyleObj';

const useStyles = makeStyles(() => ({
    scannerInput: {
        "zIndex": 1,
        "&:focus": {
            outline: "none",
        },
        "fontSize": 22,
    },
}))

interface IScanInputProps {
    customParser?: (value: string) => string
    defaultValue?: string
    error?: boolean
    errorText?: React.ReactNode
    id?: string
    InputProps?: any,
    label: React.ReactNode
    loading?: boolean
    manualEdit?: boolean
    onScan?: (value: string) => void
    onChange?: (value: string) => void
    placeholder?: string
    scanPickupEnabled?: boolean
    scanRegex?: RegExp
    submitOnScan?: boolean
    manualInputDelay?: number
    showScanButton?: boolean
    onValidationSuccess?: (data: any) => void
    triggerValidation?: boolean,
    isMultipleSPLotsChecked?: boolean,
    validSpotList?: string[]
    onDone?: (splotIds: string[]) => void
    onEnterPress?: () => void
    onInputChange?: (hasValue: boolean) => void
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
    softFocusOnMount?: boolean
}

// const DefaultInputProps = {
//     startAdornment: (
//         <InputAdornment position="start">
//             <QrCodeScannerIcon />
//         </InputAdornment>
//     ),
// }

let timeout: any;

const EMPTY_VALID_SPOT_LIST: string[] = [];

const ScanInput = forwardRef<HTMLInputElement, IScanInputProps>(({
    customParser = (value) => value,
    defaultValue = "",
    error = false,
    errorText = "",
    id,
    // InputProps = DefaultInputProps,   
    // loading = false,
    manualEdit = false,
    onScan = () => { },
    onChange,
    placeholder = "Scan or Enter Material ID",
    scanPickupEnabled = true,
    scanRegex = ALPHA_NUMERIC_REGEX,
    submitOnScan = false,
    manualInputDelay = 1000,
    showScanButton = false,
    onValidationSuccess,
    triggerValidation = false,
    isMultipleSPLotsChecked = false,
    validSpotList = EMPTY_VALID_SPOT_LIST,
    onDone,
    onEnterPress,
    onInputChange,
    onKeyDown: externalKeyDown,
    softFocusOnMount = false,
}, ref) => {
    const styles = useStyles({})
    const classes = useCommonStyles();
    const [scannedValue, setScannedValue] = useState<string>(defaultValue)
    const [validScannedValue, setValidScannedValue] = useState<string[]>([])

    // Sync internal validScannedValue with parent's validSpotList
    useEffect(() => {
        setValidScannedValue(validSpotList);
    }, [validSpotList]);

    // Function to update validScannedValue
    const updateValidScannedValue = (newList: string[]) => {
        const mergedList = isMultipleSPLotsChecked
            ? [...validScannedValue, ...newList]
            : newList;
        setValidScannedValue(mergedList);
        if (onValidationSuccess) {
            onValidationSuccess(mergedList);
        }
    };
    const internalInputRef = useRef<HTMLInputElement>(null);
    
    // Sync forwarded ref with internal ref
    useEffect(() => {
        if (ref) {
            if (typeof ref === 'function') {
                ref(internalInputRef.current);
            } else {
                ref.current = internalInputRef.current;
            }
        }
    }, [ref]);
    
    const inputRef = internalInputRef;
    const { showSnackbar } = useSnackbar();
    const [isValidating, setIsValidating] = useState(false);
    const [isSoftFocusLocked, setIsSoftFocusLocked] = useState(softFocusOnMount);
    const lastAddedValuesRef = useRef<string[]>([]);

    const removeLastAddedValues = () => {
        const lastAdded = lastAddedValuesRef.current;
        if (lastAdded.length > 0) {
            const updatedList = validScannedValue.filter(id => !lastAdded.includes(id));
            setValidScannedValue(updatedList);
            onValidationSuccess?.(updatedList);
            lastAddedValuesRef.current = [];
        }
    };

    // Validation mutation
    const { mutate: validateSPLot } = useValidateSPLot({
        mutation: {
            onSuccess: (data: any) => {
                setIsValidating(false);
                const message = data?.data?.message || 'SP Lot validated successfully';
                if (!data?.data?.is_valid) {
                    showSnackbar({ message: message || 'SP Lot validation failed', severity: 'error' });
                    setScannedValue("");
                    removeLastAddedValues();
                } else {
                    lastAddedValuesRef.current = [];
                }
            },
            onError: (error: any) => {
                setIsValidating(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error validating SP Lot';
                showSnackbar({ message: errorMessage, severity: 'error' });
                setScannedValue("");
                removeLastAddedValues();
            },
        },
    });

    // Shared keydown handler — fires on Enter OR Tab
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        externalKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (onEnterPress) {
                onEnterPress();
            } else {
                handleScanButtonClick();
                if (!isMultipleSPLotsChecked && onDone && scannedValue.trim()) {
                    const splotIds = scannedValue.split(',').map(id => id.trim()).filter(Boolean);
                    onDone(splotIds);
                }
            }
        }
    };

    // Handle scan button click
    const handleScanButtonClick = () => {
        if (!scannedValue || !scannedValue.trim()) {
            showSnackbar({ message: 'Please enter or scan a value first', severity: 'warning' });
            return;
        }

        const currentScannedValues = scannedValue.split(',').map(id => id.trim()).filter(Boolean);

        if (isMultipleSPLotsChecked) {
            const duplicates = currentScannedValues.filter(id => validScannedValue.includes(id));
            if (duplicates.length > 0) {
                showSnackbar({ message: `Duplicate SP Lot ID(s): ${duplicates.join(', ')}`, severity: 'error' });
                setScannedValue("");
                return;
            }
        }

        setIsValidating(true);
        const mergedList = isMultipleSPLotsChecked
            ? [...validScannedValue, ...currentScannedValues]
            : currentScannedValues;

        lastAddedValuesRef.current = currentScannedValues;

        if (isMultipleSPLotsChecked && mergedList.length >= 2) {
            updateValidScannedValue(currentScannedValues);
            const latestTwo = mergedList.slice(-2);
            validateSPLot({ data: { splot_ids: latestTwo } });
            setScannedValue("");
        } else if (isMultipleSPLotsChecked) {
            updateValidScannedValue(currentScannedValues);
            setScannedValue("");
            setIsValidating(false);
            lastAddedValuesRef.current = [];
        } else {
            onScan(customParser(scannedValue.trim()));
            if (!isMultipleSPLotsChecked && onDone && scannedValue.trim()) {
                const splotIds = scannedValue.split(',').map(id => id.trim()).filter(Boolean);
                onDone(splotIds);
            }
            setIsValidating(false);
            lastAddedValuesRef.current = [];
        }
    };

    // Watch for external validation trigger
    useEffect(() => {
        if (triggerValidation) {
            handleScanButtonClick();
        }
    }, [triggerValidation]);

    // update default value if it changes
    useEffect(() => {
        if (defaultValue !== scannedValue) {
            setScannedValue(defaultValue);
        }
    }, [defaultValue])

    useEffect(() => {
        try {
            timeout && clearTimeout(timeout)
            if (onScan && scannedValue) {
                timeout = setTimeout(() => {
                    onScan(customParser(scannedValue.trim()));
                }, manualEdit ? manualInputDelay : 100);
            }
        } catch (e) {
            // Error in onScan effect
        }

        return () => {
            try {
                timeout && clearTimeout(timeout)
            } catch (e) {
                // Error clearing timeout
            }
        }
    }, [scannedValue, manualEdit, onScan, manualInputDelay, customParser])

    useKeyboardTimedInput(scanPickupEnabled && !manualEdit, [
        {
            regex: scanRegex,
            callback: (e: any) => {
                const parsedValue = customParser(e);
                setScannedValue(parsedValue);

                if (submitOnScan) {
                    onScan(parsedValue.trim());
                }

                // Validate SP Lot after scan if showScanButton is enabled and value length > 1
                if (showScanButton && parsedValue && parsedValue.trim().length > 1) {
                    setIsValidating(true);
                    validateSPLot({ data: { splot_ids: [parsedValue.trim()] } });
                }
            },
        },
    ])


    useEffect(() => {
        setIsSoftFocusLocked(softFocusOnMount);
    }, [softFocusOnMount]);

    useEffect(() => {
        if (!inputRef.current) return;
        inputRef.current.focus({ preventScroll: true });
    }, []);

    const unlockSoftFocus = () => {
        if (!isSoftFocusLocked) return;
        setIsSoftFocusLocked(false);
        setTimeout(() => {
            inputRef.current?.focus({ preventScroll: true });
        }, 0);
    };


    return (
        <>
            {showScanButton ? (
                <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                    <TextField
                        id={id}
                        size="small"
                        inputRef={inputRef}
                        className={styles.scannerInput}
                        placeholder={placeholder}
                        fullWidth
                        value={scannedValue}
                        name="scan"
                        autoFocus
                        autoComplete="off"
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).textFocusHoverStyle,
                             ...MtComStyleObj(theme).textBoxValueStyle,
                            '& .MuiInputBase-root': { height: 40 },
                        })}
                        // disabled={!manualEdit}
                        slotProps={{
                            formHelperText: { sx: { ml: 0 } },
                            htmlInput: { inputMode: isSoftFocusLocked ? 'none' : 'text' },
                        }}
                        onTouchStart={unlockSoftFocus}
                        onMouseDown={unlockSoftFocus}
                        onChange={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const newVal = e.target.value;
                            if (newVal !== scannedValue) {
                                setScannedValue(newVal)
                                onChange?.(newVal.trim())
                                onInputChange?.(newVal.trim().length > 0)
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        error={error}
                        helperText={errorText || ''}
                    />
                    <Button
                        variant="contained" disableRipple
                        className={classes.greenButton}
                        startIcon={<CropFreeIcon />}                      
                        onClick={handleScanButtonClick}
                        disabled={isValidating}
                    >
                        {isValidating ? 'Validating...' : 'Scan'}
                    </Button>
                </Stack>
            ) : (
                <TextField
                    id={id}
                    size="small"
                    inputRef={inputRef}
                    className={styles.scannerInput}
                    placeholder={placeholder}
                    fullWidth
                    value={scannedValue}
                    name="scan"
                    autoFocus
                    autoComplete="off"
                    sx={(theme) => ({
                        ...MtComStyleObj(theme).textFocusHoverStyle,
                        '& .MuiInputBase-root': { height: 40 },
                    })}
                    // disabled={!manualEdit}
                    slotProps={{
                        formHelperText: { sx: { ml: 0 } },
                        htmlInput: { inputMode: isSoftFocusLocked ? 'none' : 'text' },
                    }}
                    onTouchStart={unlockSoftFocus}
                    onMouseDown={unlockSoftFocus}
                    onChange={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const newVal = e.target.value;
                        if (newVal !== scannedValue) {
                            setScannedValue(newVal)
                            onChange?.(newVal.trim())
                            onInputChange?.(newVal.trim().length > 0)
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    error={error}
                    helperText={errorText || ''}
                // InputProps={
                //     error
                //         ? {
                //               startAdornment: (
                //                   <InputAdornment position="start">
                //                       <Error color="error" />
                //                   </InputAdornment>
                //               ),
                //           }
                //         : loading
                //           ? {
                //                 startAdornment: (
                //                     <InputAdornment position="start">
                //                         <CircularProgress sx={{ width: "22px !important", height: "22px !important" }} />
                //                     </InputAdornment>
                //                 ),
                //             }
                //           : InputProps
                // }          
                />
            )}
        </>
    )
});

ScanInput.displayName = 'ScanInput';

export default ScanInput
